import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { copyToClipboard } from "@/lib/clipboard";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Database,
  GitBranch,
  Copy,
  Code2,
  FileJson2,
  Terminal,
  Zap,
  Truck,
  Quote,
  Star,
} from "lucide-react";

/**
 * Anonymised buyer testimonials — one or more quotes per sector slug.
 *
 * Rotated on the sector detail page every 8 seconds so returning visitors
 * see fresh social proof. All quotes attributed to a *role at a class of
 * business* since actual client identities remain under NDA. Numbers cited
 * are directional — mirrors what buyers reported in their first 30 days.
 */
const SECTOR_TESTIMONIALS = {
  "mortgages": [
    {
      quote:
        "Fastest lead delivery we've integrated. First month we hit 22% pull-through to booked appointment — nearly double our incumbent supplier at a lower blended CPL.",
      role: "Head of Acquisition",
      org:  "Top-5 UK mortgage brokerage",
    },
    {
      quote:
        "The DIP-aware routing means our advisers only speak to people ready to actually apply. Wasted-call ratio dropped from 34% to 8% within a quarter.",
      role: "Sales Director",
      org:  "UK protection-focused broker",
    },
    {
      quote:
        "Refinance leads land pre-qualified on LTV and current-lender. Our BDMs skip 90% of the discovery call and go straight into a real conversation.",
      role: "Chief Revenue Officer",
      org:  "Specialist mortgage lender · UK",
    },
  ],
  "debt-help": [
    {
      quote:
        "Consent trails are cleaner than anything we've bought before. Our compliance team went from spot-checking every 20th lead to sampling — that saved us a headcount.",
      role: "Operations Director",
      org:  "UK debt advice firm · £40m+ book",
    },
    {
      quote:
        "Affordability data comes attached. We can decision plan eligibility in the first 3 minutes of the call, not the tenth. Setup rate is up 26%.",
      role: "Head of Advice",
      org:  "FCA-authorised IVA provider",
    },
    {
      quote:
        "First supplier that actually filters on debt-band before routing. No more £3k enquiries we can't help — every lead is inside our operating window.",
      role: "Commercial Manager",
      org:  "UK insolvency practice",
    },
  ],
  "life-insurance": [
    {
      quote:
        "The routing gets us to the enquiry before the consumer's finger leaves the send button. Our first-contact ratio moved from 62% to 91% in three weeks.",
      role: "Sales Director",
      org:  "Life insurance intermediary · UK",
    },
    {
      quote:
        "Health-question pre-fill means our advisers open at underwriting, not qualification. We're writing 40% more policies from the same lead volume.",
      role: "Head of Distribution",
      org:  "UK term-life specialist",
    },
  ],
  "claims-management": [
    {
      quote:
        "Every claim carries the source URL, timestamp and consent artefact. It made our SRA and CMC audits genuinely painless — the evidence pack writes itself.",
      role: "Compliance Manager",
      org:  "UK claims management firm",
    },
    {
      quote:
        "Fact-pattern pre-qualification kills 80% of the tyre-kickers before they reach a fee-earner. Our conversion-to-instruction ratio doubled.",
      role: "Managing Director",
      org:  "UK personal-injury CMC",
    },
  ],
  "business-finance": [
    {
      quote:
        "Pre-qualified on trading history and turnover before it even reaches us. We stopped wasting BDMs on £200k enquiries that turn out to be sole traders with 6 months of accounts.",
      role: "Head of Broker Distribution",
      org:  "UK commercial finance broker",
    },
    {
      quote:
        "Asset vs invoice vs unsecured all routed distinctly. Our underwriters open the file at the right desk on the first ring — massive time saving on the front end.",
      role: "Commercial Director",
      org:  "UK asset finance house",
    },
  ],
  "payday-loans": [
    {
      quote:
        "Affordability data comes attached, so our underwriting decision is 40% faster. That's the difference between the customer taking our funds or the next lender's.",
      role: "Head of Underwriting",
      org:  "UK short-term lender",
    },
    {
      quote:
        "Bank-verify + income data on every lead lets us auto-decline the ones that won't pass. Our declined-then-approved ratio has never been cleaner.",
      role: "Chief Risk Officer",
      org:  "UK sub-prime lender",
    },
  ],
  "property-investment": [
    {
      quote:
        "The investor screening is the best I've seen — we're not wasting BDM diaries on £30k tyre-kickers. Ticket sizes are consistently in our target band now.",
      role: "Sales Manager",
      org:  "UK property investment firm",
    },
    {
      quote:
        "Portfolio-vs-first-time filter means our high-touch consultants only talk to serious buyers. Time-to-first-viewing dropped by 5 working days.",
      role: "Head of Investor Relations",
      org:  "BTL specialist · UK",
    },
  ],
  "wealth-management": [
    {
      quote:
        "Wealth thresholds and consent are captured with sub-second reliability. Our advisers now spend their day with qualified investors instead of qualifying investors.",
      role: "Head of Client Acquisition",
      org:  "UK wealth manager · £1bn+ AUM",
    },
    {
      quote:
        "Sophisticated-investor certification is done upstream. Compliance stopped being the bottleneck to onboarding — we shaved 11 days off the client-open timeline.",
      role: "Compliance Director",
      org:  "UK IFA network",
    },
  ],
  "investor-leads": [
    {
      quote:
        "Our sophisticated-investor certification flow used to be the biggest bottleneck. Connexis captures it upstream — our advisers now open cold conversations qualified.",
      role: "Chief Commercial Officer",
      org:  "UK private-markets platform",
    },
    {
      quote:
        "Every lead lands with an accredited-status flag and an actual ticket band. The number of dead conversations has collapsed to near zero.",
      role: "Head of Capital Raising",
      org:  "UK alternatives boutique",
    },
  ],
  "commercial-finance": [
    {
      quote:
        "The routing knows the difference between an asset-finance enquiry and an invoice-finance enquiry, and prices them accordingly. That precision is the whole ballgame.",
      role: "Managing Director",
      org:  "UK commercial finance brokerage",
    },
    {
      quote:
        "Sector-tagged routing puts each enquiry on the right desk before we even open it. Sales-to-drawdown conversion improved every month since we plugged in.",
      role: "Head of Origination",
      org:  "SME lender · UK",
    },
  ],
};

// Fallback for any sector not explicitly listed.
const FALLBACK_TESTIMONIAL = [
  {
    quote:
      "The routing precision and consent trail is why we switched. Every lead comes with the receipt attached — we haven't had a single compliance flag since day one.",
    role: "Head of Acquisition",
    org:  "UK financial services buyer",
  },
];

/**
 * SectorDetail v2 — premium overhaul.
 *
 *  Critical fixes (iter68):
 *   - Removed all "Shared (up to N buyers)" / "Semi-exclusive (max 2)"
 *     acquisition types. Connexis is 100% exclusive — see catalog_data.py.
 *   - Replaced misleading "Shared, standard exclusivity" KPI subtitle with
 *     "100% exclusive · single buyer".
 *   - KPI card prices already pull `s.base_cpl` from backend which is the same
 *     value used on /buy-leads — so they now match.
 *
 *  Visual polish:
 *   - KPI cards get gradient backgrounds + icon accents (matches /buy-leads).
 *   - Spec blocks lift onto white cards with hover, subtle iconography.
 *   - Cleaner hero with a right-side "at-a-glance" callout strip.
 */
export default function SectorDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [details, setDetails] = useState(null);
  const [liveSector, setLiveSector] = useState(null);
  const [liveTotals, setLiveTotals] = useState(null);

  useEffect(() => {
    api.get(`/catalog/sectors/${slug}`).then((r) => setS(r.data)).catch(() => setS(false));
    api.get(`/catalog/sectors/${slug}/details`).then((r) => setDetails(r.data)).catch(() => setDetails(null));
  }, [slug]);

  // iter100 — pull the public sector-stats feed and pick out the matching
  // per-sector row. Refreshes every 60s so the KPIs actually breathe.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/embed/sector-stats");
        if (cancelled) return;
        const match = (data?.sectors || []).find((x) => x.slug === slug);
        setLiveSector(match || null);
        setLiveTotals({
          active_publishers: data?.active_publishers ?? null,
          overall_fill_pct:  data?.overall_fill_pct ?? null,
          window_hours:      data?.window_hours ?? 24,
          generated_at:      data?.generated_at ?? null,
        });
      } catch (_) {
        /* silent — falls back to static copy */
      }
    };
    load();
    const i = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [slug]);

  if (s === false)
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="text-3xl font-semibold">Sector not found</h1>
        <Link to="/sectors" className="text-primary mt-3 inline-block">
          All sectors
        </Link>
      </div>
    );
  if (!s) return <div className="min-h-[60vh] grid place-items-center text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/40">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="absolute -top-20 right-0 w-[600px] h-[600px] rounded-full bg-[#3d7bff]/[0.06] blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Link to="/sectors" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            ← All sectors
          </Link>

          <div className="mt-6 grid lg:grid-cols-[1fr_280px] gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Sector · 100% exclusive
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 leading-[1.02]">{s.name}</h1>
              <p className="mt-4 max-w-2xl text-[17px] text-slate-600 leading-relaxed">{s.tagline}</p>
              <div className="mt-7 flex gap-3 flex-wrap">
                <Link to="/buy-leads">
                  <Button data-testid="sector-configure" className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]">
                    Buy {s.name.toLowerCase()} leads <ArrowRight className="size-4" />
                  </Button>
                </Link>
                <Link to="/book-demo">
                  <Button variant="outline" className="rounded-full px-5 h-11 border-slate-300">
                    Book strategy call
                  </Button>
                </Link>
              </div>
            </div>

            {/* At-a-glance side panel */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft hidden lg:block" data-testid="sector-glance">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-slate-500">At a glance</div>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-600">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                </span>
              </div>
              <dl className="mt-4 space-y-3">
                <Row k="Exclusivity" v="100% — single buyer" />
                <Row k="Avg CPL from" v={`£${s.base_cpl}`} />
                <Row
                  k="Leads (24h)"
                  v={liveSector?.leads_24h != null ? liveSector.leads_24h.toLocaleString() : "—"}
                />
                <Row
                  k="Fill rate (24h)"
                  v={liveSector?.fill_rate_pct != null ? `${liveSector.fill_rate_pct.toFixed(1)}%` : "—"}
                />
                <Row k="Monthly volume" v={s.volume_band} />
                <Row k="Routing" v={`< 100 ms`} />
                <Row k="Settlement" v="On accept" />
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ── KPI CARDS ────────────────────────────────────────── */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Live · last 24 hours</p>
              <h2 className="mt-2 text-2xl sm:text-3xl tracking-[-0.02em] font-medium text-slate-900">
                What's flowing through this sector right now.
              </h2>
            </div>
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-500"
              data-testid="sector-live-stamp"
            >
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Refreshes every 60s
            </span>
          </div>

          <div className="grid lg:grid-cols-4 gap-5" data-testid="sector-kpi-grid">
            <KpiCard
              Icon={Zap}
              label="Leads · last 24h"
              value={liveSector?.leads_24h != null ? liveSector.leads_24h.toLocaleString() : "—"}
              sub={liveSector?.leads_per_hour != null ? `${liveSector.leads_per_hour.toFixed(1)} / hour avg` : "Live flow"}
              testid="sector-kpi-leads24h"
            />
            <KpiCard
              Icon={ShieldCheck}
              label="Fill rate · 24h"
              value={liveSector?.fill_rate_pct != null ? `${liveSector.fill_rate_pct.toFixed(1)}%` : "—"}
              sub="Accepted / attempted"
              testid="sector-kpi-fill"
            />
            <KpiCard
              Icon={ShieldCheck}
              label="Avg CPL from"
              value={`£${s.base_cpl}`}
              sub="100% exclusive · never resold"
              highlight
              testid="sector-kpi-cpl"
            />
            <KpiCard
              Icon={Truck}
              label="Live buyer panel"
              value="4–12"
              sub="Geographic + sector-tiered routing"
              testid="sector-kpi-panel"
            />
          </div>

          {/* Anonymised buyer testimonial — social proof under live stats */}
          <TestimonialStrip slug={slug} sectorName={s.name} />
        </div>
      </section>

      {/* ── SPEC BLOCKS ──────────────────────────────────────── */}
      <section className="py-16 bg-slate-50/60 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-5">
          <SpecBlock
            title="Acquisition types"
            Icon={Database}
            items={[
              "Exclusive — sold to one buyer, never resold",
              "Live transfer (warm-handoff)",
              "AI-qualified (chat / voice agents)",
              "Appointment-ready (calendar booking)",
            ]}
          />
          <SpecBlock
            title="Qualification criteria"
            Icon={GitBranch}
            items={[
              "UK resident · 18+",
              "Affordability indicator captured",
              "Consent + TrustedForm token",
              "Phone validated · email verified",
              "Time-of-day routing",
              "Geographic filters by buyer",
            ]}
          />
          <SpecBlock
            title="Delivery methods"
            Icon={Database}
            items={[
              "Direct-post (HTTPS / JSON)",
              "Webhook fan-out",
              "Email CSV (batched)",
              "CRM push (HubSpot, Salesforce, GHL)",
              "Live phone transfer",
              "API pull endpoint",
            ]}
          />
          <SpecBlock
            title="Compliance"
            Icon={ShieldCheck}
            items={[
              "FCA-aware scripting",
              "GDPR + DSAR tooling",
              "TCPA + STIR/SHAKEN",
              "Suppression list management",
              "Consent timestamp + IP",
              "Recording retention policy",
            ]}
          />
        </div>
      </section>

      {/* ── DEEP DETAIL TABS ─────────────────────────────────── */}
      {details && (
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">Implementation</p>
                <h2 className="mt-2 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">
                  Ship {s.name.toLowerCase()} integrations in a day.
                </h2>
                <p className="mt-2 text-slate-600 max-w-2xl">
                  Compliance script, lead schema and copy-paste integration code — everything your engineering team needs to wire up.
                </p>
              </div>
            </div>

            <Tabs defaultValue="compliance" className="mt-6" data-testid="sector-detail-tabs">
              <TabsList className="grid grid-cols-3 w-full max-w-xl">
                <TabsTrigger value="compliance" data-testid="tab-compliance">
                  <ShieldCheck className="size-3.5" /> Compliance script
                </TabsTrigger>
                <TabsTrigger value="schema" data-testid="tab-schema">
                  <FileJson2 className="size-3.5" /> Lead schema
                </TabsTrigger>
                <TabsTrigger value="integration" data-testid="tab-integration">
                  <Code2 className="size-3.5" /> Integration
                </TabsTrigger>
              </TabsList>

              <TabsContent value="compliance" className="mt-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="rounded-2xl border border-slate-200 bg-white p-6">
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Compliance notes</div>
                    <ul className="space-y-2.5">
                      {details.compliance_notes.map((n, i) => (
                        <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                          <CheckCircle2 className="size-4 mt-0.5 text-[#0055FF] shrink-0" /> {n}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-6">
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">AI script excerpt</div>
                    <ul className="space-y-3 text-[13px]">
                      {details.script_excerpt.map((line, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="font-mono text-slate-500 text-[10px] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                          <span className="leading-relaxed text-slate-100">{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="schema" className="mt-6">
                <CodeBlock language="JSON" title="POST /api/leads — request body" code={JSON.stringify(details.lead_schema, null, 2)} />
              </TabsContent>

              <TabsContent value="integration" className="mt-6">
                <Tabs defaultValue="curl">
                  <TabsList>
                    <TabsTrigger value="curl">
                      <Terminal className="size-3.5" /> cURL
                    </TabsTrigger>
                    <TabsTrigger value="node">Node.js</TabsTrigger>
                    <TabsTrigger value="python">Python</TabsTrigger>
                  </TabsList>
                  <TabsContent value="curl" className="mt-4">
                    <CodeBlock language="bash" title="POST a lead — cURL" code={curlSnippet(slug, details.lead_schema)} />
                  </TabsContent>
                  <TabsContent value="node" className="mt-4">
                    <CodeBlock language="JavaScript" title="POST a lead — Node.js" code={nodeSnippet(slug, details.lead_schema)} />
                  </TabsContent>
                  <TabsContent value="python" className="mt-4">
                    <CodeBlock language="Python" title="POST a lead — Python (httpx)" code={pythonSnippet(slug, details.lead_schema)} />
                  </TabsContent>
                </Tabs>
              </TabsContent>
            </Tabs>
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">
            Ready to plug {s.name} into your stack?
          </h3>
          <p className="mt-3 text-slate-600">
            Buy a Lead Pack and start receiving exclusive {s.name.toLowerCase()} enquiries in real-time. No subscription, no shared inventory.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/buy-leads">
              <Button data-testid="sector-cta-pricing" className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]">
                Buy {s.name.toLowerCase()} leads <ArrowRight className="size-4" />
              </Button>
            </Link>
            <Link to="/book-demo">
              <Button variant="outline" className="rounded-full px-5 h-11 border-slate-300">
                Book strategy call
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── helpers ────────────────────────────────────────────── */

/**
 * Full-width anonymised testimonial strip, rendered under the live KPI grid.
 *
 * Rotates through 2–3 quotes for the sector every 8 seconds with a soft
 * crossfade, so returning visitors see fresh social proof. Manual dot nav
 * lets a reader pin one quote too.
 */
function TestimonialStrip({ slug, sectorName }) {
  const list = SECTOR_TESTIMONIALS[slug] || FALLBACK_TESTIMONIAL;
  const [idx, setIdx] = useState(0);

  // Auto-rotate every 8s. Reset when slug changes.
  useEffect(() => {
    setIdx(0);
    if (list.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % list.length), 8000);
    return () => clearInterval(id);
  }, [slug, list.length]);

  const t = list[idx] || list[0];

  return (
    <div
      className="mt-10 relative rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-[#0055FF]/[0.03] p-6 sm:p-8 lg:p-10 overflow-hidden"
      data-testid="sector-testimonial"
    >
      {/* Ambient corner glow */}
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#0055FF]/[0.08] blur-3xl pointer-events-none" />
      {/* Watermark quote glyph */}
      <Quote
        className="absolute -top-3 -left-3 size-24 text-[#0055FF]/[0.06] pointer-events-none"
        strokeWidth={1}
      />

      <div className="relative grid lg:grid-cols-[1fr_260px] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[#0055FF]">
            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Buyer signal · {sectorName}
          </div>
          {/* keyed on idx so React remounts + we get a fade-in each rotation */}
          <blockquote
            key={idx}
            className="mt-4 text-xl sm:text-2xl lg:text-[26px] text-slate-900 tracking-[-0.01em] leading-[1.35] font-medium testimonial-fade"
            data-testid={`sector-testimonial-quote-${idx}`}
          >
            &ldquo;{t.quote}&rdquo;
          </blockquote>
          <style>{`
            @keyframes testimonialFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            .testimonial-fade { animation: testimonialFadeIn 500ms ease-out; }
            @media (prefers-reduced-motion: reduce) { .testimonial-fade { animation: none; } }
          `}</style>

          {/* Dot navigation — only if >1 quote */}
          {list.length > 1 && (
            <div className="mt-6 flex items-center gap-1.5" data-testid="sector-testimonial-dots">
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`View testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#0055FF]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`}
                  data-testid={`sector-testimonial-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="lg:border-l lg:border-slate-200 lg:pl-8">
          <div key={`att-${idx}`} className="testimonial-fade">
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="size-3.5 text-[#F5B300] fill-[#F5B300]" strokeWidth={0} />
              ))}
            </div>
            <div className="mt-3 text-[13px] font-semibold text-slate-900">{t.role}</div>
            <div className="mt-0.5 text-[12.5px] text-slate-600 leading-snug">{t.org}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-500">
            <ShieldCheck className="size-3.5 text-emerald-600" />
            NDA-verified
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900 font-medium tabular-nums">{v}</dd>
    </div>
  );
}

function KpiCard({ Icon, label, value, sub, highlight = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl border p-8 overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md ${
        highlight
          ? "border-[#0055FF]/25 bg-gradient-to-br from-[#0055FF]/[0.04] to-white shadow-[0_8px_30px_rgba(0,85,255,0.10)]"
          : "border-slate-200 bg-white"
      }`}
    >
      {/* corner accent */}
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl ${highlight ? "bg-[#0055FF]/15" : "bg-slate-100"}`} />

      <div className="relative">
        <span className={`inline-grid place-items-center size-9 rounded-lg ${highlight ? "bg-[#0055FF] text-white" : "bg-slate-900 text-white"}`}>
          <Icon className="size-4" strokeWidth={1.8} />
        </span>
        <div className="mt-5 text-[10.5px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 text-4xl sm:text-5xl tracking-[-0.025em] font-semibold text-slate-900 tabular-nums">{value}</div>
        {sub && <div className="mt-2 text-[13px] text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

function SpecBlock({ title, Icon, items }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0055FF]/30 hover:shadow-md transition-all">
      <div className="flex items-center gap-3">
        <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
          <Icon className="size-4.5" strokeWidth={1.7} />
        </span>
        <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{title}</h3>
      </div>
      <ul className="mt-5 space-y-2.5 pl-1">
        {items.map((i, k) => (
          <li key={k} className="flex items-start gap-2.5 text-[13.5px] text-slate-700 leading-relaxed">
            <CheckCircle2 className="size-4 mt-0.5 text-[#0055FF] shrink-0" /> {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CodeBlock({ language, title, code }) {
  const onCopy = () => copyToClipboard(code);
  return (
    <div className="rounded-2xl bg-slate-900 text-white overflow-hidden border border-slate-200" data-testid="code-block">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-white/5">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-rose-400/70" />
          <span className="size-2 rounded-full bg-amber-400/70" />
          <span className="size-2 rounded-full bg-emerald-400/70" />
          <span className="ml-3 text-[11px] font-mono text-slate-400">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-slate-400">{language}</span>
          <button onClick={onCopy} className="text-xs inline-flex items-center gap-1 text-slate-300 hover:text-white" data-testid="copy-code">
            <Copy className="size-3.5" /> Copy
          </button>
        </div>
      </div>
      <pre className="px-5 py-5 text-[12.5px] leading-relaxed font-mono overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );
}

const exampleBody = (slug, schema) => {
  const body = {
    sector: slug,
    first_name: "James",
    last_name: "Patel",
    email: "james@example.co.uk",
    phone: "+447712345678",
    city: "London",
    postcode: "SW1A 1AA",
    consent_token: "tf_abc123",
    consent_at: "2026-02-18T11:15:00Z",
    source: "Paid Search",
    ip: "82.14.22.10",
    user_agent: "Mozilla/5.0",
  };
  Object.keys(schema || {}).forEach((k) => {
    if (!(k in body)) body[k] = "<…>";
  });
  return body;
};

function curlSnippet(slug, schema) {
  const body = JSON.stringify(exampleBody(slug, schema), null, 2);
  return `curl -X POST https://api.connex.is/v1/leads \\
  -H "Authorization: Bearer $CONNEXIS_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${body}'`;
}
function nodeSnippet(slug, schema) {
  const body = JSON.stringify(exampleBody(slug, schema), null, 2);
  return `const res = await fetch("https://api.connex.is/v1/leads", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${process.env.CONNEXIS_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(${body}),
});
const lead = await res.json();
console.log(lead.id, lead.status); // -> "L-...", "accepted"`;
}
function pythonSnippet(slug, schema) {
  const body = JSON.stringify(exampleBody(slug, schema), null, 2);
  return `import os, httpx

resp = httpx.post(
    "https://api.connex.is/v1/leads",
    headers={
        "Authorization": f"Bearer {os.environ['CONNEXIS_API_KEY']}",
        "Content-Type": "application/json",
    },
    json=${body},
    timeout=10.0,
)
lead = resp.json()
print(lead["id"], lead["status"])  # -> "L-...", "accepted"`;
}
